/*
LIME LABS COMMERCE LAYER
Component: PDP Related Editorial Rail
Project: La Troupe
Version: 5.2
Status: LLCL-QA

Progressive enhancement only.
- Auto-advances one recommendation at a time.
- Pauses on hover, focus, drag/touch and user navigation.
- Stops while off-screen or when the tab is hidden.
- Respects prefers-reduced-motion.
*/

(() => {
  'use strict';

  if (window.__LLCL_PDP_RELATED_V52__) return;
  window.__LLCL_PDP_RELATED_V52__ = true;

  const ROOT = 'product-recommendations.ll-pdp-v52-related';
  const SHELL = '[data-ll-pdp-v52-related]';
  const RAIL = '[data-ll-pdp-v52-related-rail]';
  const CARD = '[data-ll-pdp-v52-related-card]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const USER_PAUSE_MS = 6500;

  const pad = value => String(Math.max(0, value)).padStart(2, '0');

  const bind = root => {
    if (!root) return;

    const shell = root.querySelector(SHELL);
    const rail = root.querySelector(RAIL);
    if (!shell || !rail || shell.dataset.llPdpV52Bound === 'true') return;

    const cards = Array.from(rail.querySelectorAll(CARD));
    if (cards.length < 2) return;

    shell.dataset.llPdpV52Bound = 'true';

    const prev = shell.querySelector('[data-ll-pdp-v52-related-prev]');
    const next = shell.querySelector('[data-ll-pdp-v52-related-next]');
    const current = shell.querySelector('[data-ll-pdp-v52-related-current]');
    const total = shell.querySelector('[data-ll-pdp-v52-related-total]');
    const progress = shell.querySelector('[data-ll-pdp-v52-related-progress]');

    const autoplayEnabled = shell.dataset.autoscroll !== 'false';
    const intervalMs = Math.max(3000, Number(shell.dataset.autoscrollInterval || 5) * 1000);

    let timer = null;
    let hovered = false;
    let focused = false;
    let pointerActive = false;
    let visible = true;
    let userPauseUntil = 0;

    const maxScroll = () => Math.max(0, rail.scrollWidth - rail.clientWidth);

    const currentIndex = () => {
      const left = rail.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((card, index) => {
        const distance = Math.abs(card.offsetLeft - left);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    };

    const updateUI = () => {
      const max = maxScroll();
      const index = currentIndex();

      if (current) current.textContent = pad(index + 1);
      if (total) total.textContent = pad(cards.length);
      if (progress) {
        const ratio = max <= 1 ? 1 : Math.min(1, Math.max(0, rail.scrollLeft / max));
        progress.style.transform = `scaleX(${ratio})`;
      }

      if (prev) prev.disabled = max <= 1 || rail.scrollLeft <= 2;
      if (next) next.disabled = max <= 1 || rail.scrollLeft >= max - 2;
    };

    const scrollToCard = (index, behavior = 'smooth') => {
      const safeIndex = Math.min(cards.length - 1, Math.max(0, index));
      const max = maxScroll();
      const target = Math.min(max, Math.max(0, cards[safeIndex].offsetLeft));

      rail.scrollTo({
        left: target,
        behavior: reducedMotion.matches ? 'auto' : behavior
      });
    };

    const markUserInteraction = (duration = USER_PAUSE_MS) => {
      userPauseUntil = Math.max(userPauseUntil, performance.now() + duration);
      schedule();
    };

    const moveManual = direction => {
      markUserInteraction();
      const index = currentIndex();
      scrollToCard(index + direction);
    };

    const isPaused = () => (
      !autoplayEnabled ||
      reducedMotion.matches ||
      document.hidden ||
      !visible ||
      hovered ||
      focused ||
      pointerActive ||
      performance.now() < userPauseUntil
    );

    const autoStep = () => {
      timer = null;
      if (!root.isConnected) return;

      if (isPaused()) {
        schedule(700);
        return;
      }

      const max = maxScroll();
      if (max <= 2) {
        schedule();
        return;
      }

      if (rail.scrollLeft >= max - 3) {
        rail.scrollTo({
          left: 0,
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      } else {
        const index = currentIndex();
        const nextIndex = Math.min(cards.length - 1, index + 1);
        const nextTarget = Math.min(max, cards[nextIndex].offsetLeft);

        if (nextTarget <= rail.scrollLeft + 2) {
          rail.scrollTo({
            left: max,
            behavior: reducedMotion.matches ? 'auto' : 'smooth'
          });
        } else {
          scrollToCard(nextIndex);
        }
      }

      schedule();
    };

    function schedule(delay = intervalMs) {
      if (timer) window.clearTimeout(timer);
      timer = null;

      if (!autoplayEnabled || reducedMotion.matches || !root.isConnected) return;
      timer = window.setTimeout(autoStep, delay);
    }

    prev?.addEventListener('click', () => moveManual(-1));
    next?.addEventListener('click', () => moveManual(1));

    rail.addEventListener('scroll', updateUI, { passive: true });
    rail.addEventListener('wheel', () => markUserInteraction(), { passive: true });
    rail.addEventListener('touchstart', () => markUserInteraction(), { passive: true });
    rail.addEventListener('click', () => markUserInteraction(), true);

    rail.addEventListener('keydown', event => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveManual(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveManual(-1);
      } else {
        markUserInteraction();
      }
    });

    rail.addEventListener('pointerdown', () => {
      pointerActive = true;
      rail.classList.add('is-interacting');
      markUserInteraction();
    }, { passive: true });

    const finishPointer = () => {
      pointerActive = false;
      rail.classList.remove('is-interacting');
      markUserInteraction();
    };

    rail.addEventListener('pointerup', finishPointer, { passive: true });
    rail.addEventListener('pointercancel', finishPointer, { passive: true });

    rail.addEventListener('mouseenter', () => {
      hovered = true;
    }, { passive: true });

    rail.addEventListener('mouseleave', () => {
      hovered = false;
      schedule(900);
    }, { passive: true });

    root.addEventListener('focusin', () => {
      focused = true;
    });

    root.addEventListener('focusout', event => {
      if (!root.contains(event.relatedTarget)) {
        focused = false;
        schedule(900);
      }
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting);
        if (visible) schedule(900);
      }, { threshold: 0.18, rootMargin: '120px 0px' });

      observer.observe(root);
    }

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(updateUI);
      observer.observe(rail);
    }

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) schedule(900);
    });

    reducedMotion.addEventListener?.('change', () => {
      if (reducedMotion.matches && timer) {
        window.clearTimeout(timer);
        timer = null;
      } else {
        schedule(900);
      }
    });

    updateUI();
    schedule();
  };

  const observeAsyncRoot = root => {
    if (!root || root.dataset.llPdpV52Observer === 'true') return;

    if (root.querySelector(SHELL)?.querySelector(CARD)) {
      bind(root);
      return;
    }

    root.dataset.llPdpV52Observer = 'true';

    const observer = new MutationObserver(() => {
      if (!root.querySelector(SHELL)?.querySelector(CARD)) return;
      observer.disconnect();
      delete root.dataset.llPdpV52Observer;
      bind(root);
    });

    observer.observe(root, { childList: true, subtree: true });
  };

  const boot = (scope = document) => {
    if (scope.matches?.(ROOT)) observeAsyncRoot(scope);
    scope.querySelectorAll?.(ROOT).forEach(observeAsyncRoot);
  };

  document.addEventListener('shopify:section:load', event => boot(event.target || document));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }
})();
